import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CustomButton from "@/components/customButton";
import { useTypedSelector } from "@/hooks";
import { useDispatch } from "react-redux";
import {
  useFetchEmailTemplateQuery,
  useUpdateEmailTemplateMutation,
} from "@/services";
import { message } from "antd";

import { CloseOutlined } from "@ant-design/icons";
import customErrorMessage from "@/components/CustomErrorMessage";

const EmailTemplate = () => {
  const dispatch = useDispatch();
  // Extract company ID safely from authenticated user
  const companyId = useTypedSelector(state => state.auth?.user?.company?.id);
  // Inside your component
  const [activeEditor, setActiveEditor] = useState("start");
  const [emailStart, setEmailStart] = useState("");
  const [emailEnd, setEmailEnd] = useState("");
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [newCcEmail, setNewCcEmail] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // Fetch from API
  const {
    data: emailTemplate,
    isLoading,
    error,
  } = useFetchEmailTemplateQuery(companyId, {
    skip: !companyId,
  });

  const [updateEmailTemplate, { isLoading: isUpdating }] =
    useUpdateEmailTemplateMutation();
  // Update editor state when API data is fetched
  useEffect(() => {
    if (emailTemplate?.data) {
      setEmailStart(emailTemplate.data.email_body_start || "");
      setEmailEnd(emailTemplate.data.email_body_end || "");
      setCcEmails(emailTemplate.data.cc_emails || []);
      setLogoPreview(emailTemplate.data.logo_url || null);
    }
  }, [emailTemplate]);

  const handleSaveTemplate = async () => {
    try {
      await updateEmailTemplate({
        companyId: companyId,
        body: {
          email_body_start: emailStart,
          email_body_end: emailEnd,
          cc_emails: ccEmails,
          company_logo: logoPreview,
        },
      }).unwrap();

      message.success("Email template updated successfully!");
    } catch (err) {
      customErrorMessage("Failed to update the email template!");
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
    ],
  };
  return (

    <div className="bg-white p-4 sm:p-5">
      <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center">
        <div className="title text-white font-medium text-3xl">
          Email Template
        </div>

        <div className="flex flex-row justify-between gap-2">

          <CustomButton
            isLoading={false}
            onClick={() => setActiveEditor("start")}
            btnName="Email Introduction"
            customClasses={`shadow-md font-medium border py-1 px-6 !border-themeBorder ${activeEditor === "start"
              ? "bg-gray-800 text-white"
              : "text-black"
              }`}
            iconPosition="left"
          />
          <CustomButton
            isLoading={false}
            onClick={() => setActiveEditor("end")}
            btnName="Email Conclusion"
            customClasses={`shadow-md font-medium border py-1 px-6 !border-themeBorder ${activeEditor === "end"
              ? "bg-gray-800 text-white"
              : "text-black"
              }`}
            iconPosition="left"
          />

          <CustomButton
            isLoading={false}
            onClick={() => setActiveEditor("settings")}
            btnName="Settings"
            customClasses={`shadow-md font-medium border py-1 px-6 !border-themeBorder ${activeEditor === "settings"
              ? "bg-gray-800 text-white"
              : "text-black"
              }`}
            iconPosition="left"
          />

        </div>
      </div>

      <div className="jobCreationListing mt-6 bg-revampBlue px-14 py-4 rounded-lg">
        <div className="my-6">


          <div className="my-6">
            {/* <h3 className="text-xl font-semibold mb-2">
              {activeEditor === "start"
                ? "Email Introduction"
                : activeEditor === "end"
                  ? "Email Conclusion"
                  : "Settings"}
            </h3> */}
            <div className="border-2 rounded-lg overflow-hidden bg-white">
              {activeEditor === "start" ? (
                <ReactQuill
                  key="start-editor"
                  theme="snow"
                  value={emailStart}
                  onChange={setEmailStart}
                  modules={modules}
                  className="h-64 border-none"
                />
              ) : activeEditor === "end" ? (
                <ReactQuill
                  key="end-editor"
                  theme="snow"
                  value={emailEnd}
                  onChange={setEmailEnd}
                  modules={modules}
                  className="h-64 border-none"
                />
              ) : (
                <div className="space-y-6 p-4">
                  {/* Upload Logo Section */}
                  <div>
                    <label className="block text-lg font-semibold text-gray-800 mb-2">
                      Company Logo
                    </label>
                    {logoPreview ? (
                      <div className="relative inline-block w-40 h-40">
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          className="w-full h-full object-contain border rounded shadow"
                        />
                        <CloseOutlined
                          onClick={() => {
                            setLogoFile(null);
                            setLogoPreview(null);
                          }}
                          className="absolute top-0 right-0 bg-white rounded-full p-1 shadow cursor-pointer text-red-500 hover:text-red-700"
                        />
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setLogoFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setLogoPreview(reader.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-themeBlue"
                      />
                    )}
                  </div>

                  {/* CC Emails Section */}
                  <label className="block text-lg font-semibold text-gray-800">
                    CC Emails
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {ccEmails.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-blue-100 text-themeBlue px-3 py-1 rounded-full shadow-sm"
                      >
                        <span className="mr-2 text-sm">{email}</span>
                        <CloseOutlined
                          onClick={() =>
                            setCcEmails(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-themeBlue hover:text-red-500 cursor-pointer text-xs"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                    <input
                      type="email"
                      placeholder="Enter email"
                      value={newCcEmail}
                      onChange={e => setNewCcEmail(e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-themeBlue"
                    />
                    <button
                      onClick={() => {
                        if (
                          newCcEmail &&
                          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCcEmail)
                        ) {
                          if (!ccEmails.includes(newCcEmail)) {
                            setCcEmails([...ccEmails, newCcEmail]);
                            setNewCcEmail("");
                          } else {
                            message.warning("This email is already added.");
                          }
                        } else {
                          customErrorMessage("Please enter a valid email address.");
                        }
                      }}
                      className="bg-revampBlue hover:bg-revampDarkBlue text-white px-6 py-2 rounded w-full md:w-auto"
                    >
                      Add Email
                    </button>
                  </div>
                </div>
              )}
            </div>

            {activeEditor != "settings" && (
              <p className="mt-4 text-white">
                You can includes following #hashtags tags in your email
                template.
              </p>
            )}
          </div>
        </div>


      </div>
        <div className="flex justify-center mt-4 mx-auto">
          <button
            className={`bg-revampOrange text-white text-sm py-2 px-6 rounded-md ${isUpdating ? "opacity-50 cursor-not-allowed" : ""
              }`}
            onClick={handleSaveTemplate}
            disabled={isUpdating}
          >
            {isUpdating ? "Loading..." : "Save Template"}
          </button>
        </div>


    </div>

  );
};

export default EmailTemplate;
